import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import SingleEvent from '../../classes/events/SingleEvent';
import EventUsersActionBar from './users/EventUsersActionBar';
import PendingApplicationsList from './users/PendingApplicationsList';
import JoinedUsersList from './users/JoinedUsersList';
import { useCreateNotification } from '../../hooks/useCreateNotification';
import { isEventNotFinishedYet } from '../../utils/eventSchedule';
import Loading from '../layout/Loading';
import './EventUsersList.css';

// ============ Event Users List Cache ============
const USERS_CACHE_KEY = 'eventUsersListCache';
const USERS_CACHE_TTL = 1000 * 60 * 5; // 5 minut TTL (krótszy bo dane się często zmieniają)

const getUsersCache = () => {
  try {
    const cached = localStorage.getItem(USERS_CACHE_KEY);
    if (!cached) return {};
    return JSON.parse(cached);
  } catch {
    return {};
  }
};

const saveUsersCache = (cache) => {
  try {
    const entries = Object.entries(cache);
    if (entries.length > 20) {
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      entries.slice(0, entries.length - 20).forEach(([k]) => delete cache[k]);
    }
    localStorage.setItem(USERS_CACHE_KEY, JSON.stringify(cache));
  } catch (err) {
    console.warn('Failed to save event users cache:', err);
  }
};

const getCachedUsers = (eventId) => {
  if (!eventId) return null;
  const cache = getUsersCache();
  const entry = cache[eventId];
  if (!entry) return null;
  if (Date.now() - entry.timestamp > USERS_CACHE_TTL) {
    delete cache[eventId];
    saveUsersCache(cache);
    return null;
  }
  return entry.data;
};

const setCachedUsers = (eventId, data) => {
  if (!eventId) return;
  const cache = getUsersCache();
  cache[eventId] = { data, timestamp: Date.now() };
  saveUsersCache(cache);
};

/**
 * Komponent zarządzający listą użytkowników eventu
 * Używa wirtualnego obiektu stanu do synchronizacji danych w czasie rzeczywistym
 */
function EventUsersList({ eventId, event, currentUser, isHost, onStatusChange }) {
  const { t } = useTranslation(['events', 'shared']);
  const createNotification = useCreateNotification({ cooldownMinutes: 0 }); // No cooldown for kicks
  
  // Załaduj dane z cache przy inicjalizacji
  const cachedData = eventId ? getCachedUsers(eventId) : null;
  
  // Wirtualny obiekt stanu - wszystkie dane w jednym miejscu
  const [virtualState, setVirtualState] = useState({
    // Dane eventu
    event: event || null,
    maxParticipants: event?.max_participants || 0,
    
    // Uczestnicy (z cache lub puste)
    participants: cachedData?.participants || [],
    participantsCount: cachedData?.participantsCount || 0,
    
    // Zgłoszenia oczekujące
    pendingApplications: cachedData?.pendingApplications || [],
    
    // Status użytkownika
    userApplication: cachedData?.userApplication || null,
    isParticipant: cachedData?.isParticipant || false,
    
    // Status synchronizacji - nie pokazuj loading jeśli mamy cache
    loading: !cachedData,
    error: null,
    lastUpdate: null
  });

  const channelRef = useRef(null);
  const updateTimeoutRef = useRef(null);
  const eventIsOpenForParticipation = isEventNotFinishedYet(virtualState.event || event);

  // Funkcja aktualizująca wirtualny stan lokalnie
  const updateVirtualState = useCallback((updates) => {
    setVirtualState(prev => {
      const newState = { ...prev, ...updates, lastUpdate: new Date() };
      
      // Oblicz liczbę uczestników z danych w pamięci
      const participantsCount = newState.participants?.length || 0;
      
      const updatedState = {
        ...newState,
        participantsCount,
        // Aktualizuj miejsca dostępne (np. 31/33)
        availableSpots: newState.maxParticipants - participantsCount
      };
      return updatedState;
    });
  }, []);

  // Funkcja czyszcząca subskrypcje
  const cleanup = useCallback(() => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
  }, []);

  // Ładowanie początkowych danych
  const loadInitialData = useCallback(async () => {
    try {
      // Pokaż loading tylko jeśli nie mamy danych z cache
      if (virtualState.participants.length === 0) {
        updateVirtualState({ loading: true, error: null });
      }

      // Załaduj wszystkie dane równolegle
      const [participantsResult, applicationsResult, userApplicationResult] = await Promise.all([
        SingleEvent.loadParticipants(eventId),
        isHost ? SingleEvent.loadApplications(eventId, 'pending') : Promise.resolve({ data: [], error: null }),
        currentUser?.id ? SingleEvent.loadUserApplication(eventId, currentUser.id) : Promise.resolve({ data: null, error: null })
      ]);

      // Sprawdź czy użytkownik jest uczestnikiem
      let isParticipant = false;
      if (currentUser?.id) {
        isParticipant = await SingleEvent.isParticipantInDB(eventId, currentUser.id);
      }

      const participants = participantsResult.data || [];
      const pendingApplications = applicationsResult.data || [];
      const userApplication = userApplicationResult.data;

      // Aktualizuj wirtualny stan
      updateVirtualState({
        participants,
        participantsCount: participants.length,
        pendingApplications,
        userApplication,
        isParticipant,
        loading: false,
        error: null
      });

      // Zapisz do cache
      setCachedUsers(eventId, {
        participants,
        participantsCount: participants.length,
        pendingApplications,
        userApplication,
        isParticipant
      });
    } catch (err) {
      console.error('EventUsersList: Error loading initial data:', err);
      updateVirtualState({
        loading: false,
        error: err.message || t('shared.common.error')
      });
    }
  }, [eventId, isHost, currentUser?.id, updateVirtualState, t, virtualState.participants.length]);

  // Obsługa zmian w uczestnikach (real-time)
  const handleParticipantChange = useCallback(async (payload) => {
    // Dla uproszczenia, przeładuj dane uczestników
    // W przyszłości można zoptymalizować aby aktualizować tylko zmienione elementy
    const { data, error } = await SingleEvent.loadParticipants(eventId);
    
    if (!error && data) {
      setVirtualState(prev => {
        const participantsCount = data.length;
        let isParticipant = false;
        if (currentUser?.id) {
          isParticipant = data.some(p => p.user_id === currentUser.id);
        }
        
        return {
          ...prev,
          participants: data,
          participantsCount,
          isParticipant
        };
      });
    }
  }, [eventId, currentUser?.id]);

  // Obsługa zmian w zgłoszeniach (real-time)
  const handleApplicationChange = useCallback(async (payload) => {
    // Przeładuj dane zgłoszeń i zgłoszenie użytkownika
    const [applicationsResult, userApplicationResult] = await Promise.all([
      isHost ? SingleEvent.loadApplications(eventId, 'pending') : Promise.resolve({ data: [], error: null }),
      currentUser?.id ? SingleEvent.loadUserApplication(eventId, currentUser.id) : Promise.resolve({ data: null, error: null })
    ]);

    setVirtualState(prev => ({
      ...prev,
      pendingApplications: applicationsResult.data || [],
      userApplication: userApplicationResult.data
    }));
  }, [eventId, isHost, currentUser?.id]);

  // Konfiguracja subskrypcji real-time
  const setupRealtimeSubscription = useCallback(() => {
    cleanup();

    const channel = supabase
      .channel(`event-users-${eventId}`)
      // Subskrypcja zmian w uczestnikach
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'event_participants',
          filter: `event_id=eq.${eventId}`
        },
        (payload) => {
          handleParticipantChange(payload);
        }
      )
      // Subskrypcja zmian w zgłoszeniach
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'event_applications',
          filter: `event_id=eq.${eventId}`
        },
        (payload) => {
          handleApplicationChange(payload);
        }
      )
      .subscribe();

    channelRef.current = channel;
  }, [eventId, handleParticipantChange, handleApplicationChange, cleanup]);

  // Ładowanie początkowych danych
  useEffect(() => {
    if (eventId) {
      loadInitialData().catch(err => {
        console.error('EventUsersList: Error in loadInitialData:', err);
      });
      setupRealtimeSubscription();

      return () => {
        cleanup();
      };
    }
  }, [eventId, currentUser?.id, loadInitialData, setupRealtimeSubscription, cleanup]);

  // Obsługa dołączania użytkownika
  const handleJoin = async (message) => {
    if (!eventIsOpenForParticipation) {
      alert(t('events.join.eventClosed'));
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert(t('events.join.notLoggedIn'));
        return;
      }

      // Sprawdź czy wydarzenie jest anulowane
      if (event?.is_cancelled) {
        alert(t('events.eventCancelled', 'Wydarzenie zostało anulowane'));
        return;
      }

      // Sprawdź czy już jest uczestnikiem
      if (virtualState.isParticipant) {
        return;
      }

      // Sprawdź czy już ma zgłoszenie
      if (virtualState.userApplication?.status === 'pending') {
        alert(t('events.join.alreadyApplied'));
        return;
      }

      // Utwórz lub zaktualizuj zgłoszenie
      if (virtualState.userApplication) {
        const { data: updatedApplication, error } = await supabase
          .from('event_applications')
          .update({
            status: 'pending',
            message: message?.trim() || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', virtualState.userApplication.id)
          .select('id')
          .single();

        if (error) throw error;

        // UWAGA: Powiadomienie dla hosta jest tworzone automatycznie przez trigger w bazie danych
        // (trigger_notify_new_application w pliku 012-create-notifications-table.sql)
        // Dodatkowo emitujemy custom event żeby odświeżyć licznik hosta natychmiast
        if (virtualState.event?.host_id) {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('notificationsChanged', {
              detail: { 
                userId: virtualState.event.host_id, 
                action: 'new_application',
                eventId: eventId,
                applicationId: updatedApplication?.id
              }
            }));
          }, 300);
        }
      } else {
        // Sprawdź czy rekord już istnieje (np. po wycofaniu)
        const { data: existingApplication } = await supabase
          .from('event_applications')
          .select('id')
          .eq('event_id', eventId)
          .eq('user_id', user.id)
          .maybeSingle();

        if (existingApplication) {
          // Zaktualizuj istniejący rekord
          const { data: updatedApplication, error } = await supabase
            .from('event_applications')
            .update({
              status: 'pending',
              message: message?.trim() || null,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingApplication.id)
            .select('id')
            .single();

          if (error) throw error;

          // UWAGA: Powiadomienie dla hosta jest tworzone automatycznie przez trigger w bazie danych
          // (trigger_notify_new_application w pliku 012-create-notifications-table.sql)
          // Dodatkowo emitujemy custom event żeby odświeżyć licznik hosta natychmiast
          if (virtualState.event?.host_id) {
            setTimeout(() => {
              const event = new CustomEvent('notificationsChanged', {
                detail: { 
                  userId: virtualState.event.host_id, 
                  action: 'new_application',
                  eventId: eventId,
                  applicationId: updatedApplication?.id
                },
                bubbles: true,
                cancelable: true
              });
              
              window.dispatchEvent(event);
              document.dispatchEvent(event);
            }, 300);
          }
        } else {
          // Dodaj nowy rekord
          const { data: newApplication, error } = await supabase
            .from('event_applications')
            .insert({
              event_id: eventId,
              user_id: user.id,
              status: 'pending',
              message: message?.trim() || null
            })
            .select('id')
            .single();

          if (error) throw error;

          // UWAGA: Powiadomienie dla hosta jest tworzone automatycznie przez trigger w bazie danych
          // (trigger_notify_new_application w pliku 012-create-notifications-table.sql)
          // Dodatkowo emitujemy custom event żeby odświeżyć licznik hosta natychmiast
          if (virtualState.event?.host_id) {
            // Poczekaj chwilę żeby trigger zdążył utworzyć powiadomienie
            setTimeout(() => {
              // Emituj event dla hosta żeby odświeżyć jego licznik
              // Używamy window.dispatchEvent żeby działało nawet gdy komponent jest w popupie
              const event = new CustomEvent('notificationsChanged', {
                detail: { 
                  userId: virtualState.event.host_id, 
                  action: 'new_application',
                  eventId: eventId,
                  applicationId: newApplication?.id
                },
                bubbles: true, // Ważne: event może się propagować
                cancelable: true
              });
              
              // Emituj na window - działa globalnie niezależnie od kontekstu
              window.dispatchEvent(event);
              
              // Dodatkowo emituj na document jako backup
              document.dispatchEvent(event);
            }, 300);
          }
        }
      }

      // Poczekaj chwilę, aby baza danych zdążyła się zaktualizować
      await new Promise(resolve => setTimeout(resolve, 100));

      // Powiadom parent component o zmianie statusu
      if (onStatusChange) {
        await onStatusChange();
      }

      // Stan zostanie zaktualizowany przez real-time subscription
    } catch (err) {
      console.error('Error joining event:', err);
      alert(err.message || t('events.join.error'));
    }
  };

  // Obsługa wycofania zgłoszenia
  const handleWithdraw = async () => {
    if (!eventIsOpenForParticipation) {
      alert(t('events.leave.eventClosed'));
      return;
    }

    if (!window.confirm(t('events.leave.confirm'))) {
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert(t('events.join.notLoggedIn'));
        return;
      }

      // Jeśli jest uczestnikiem, usuń z uczestników
      if (virtualState.isParticipant) {
        await supabase
          .from('event_participants')
          .update({ status: 'withdrawn', updated_at: new Date().toISOString() })
          .eq('event_id', eventId)
          .eq('user_id', user.id)
          .eq('status', 'active');
      }

      // Zaktualizuj zgłoszenie
      if (virtualState.userApplication) {
        const { error } = await supabase
          .from('event_applications')
          .update({ status: 'withdrawn', updated_at: new Date().toISOString() })
          .eq('id', virtualState.userApplication.id);
        
        if (error) throw error;
      }

      // Pobierz dane użytkownika do wiadomości systemowej (tylko jeśli był uczestnikiem)
      if (virtualState.isParticipant) {
        const { data: userData } = await supabase
          .from('users')
          .select('nickname, name, first_name, last_name')
          .eq('id', user.id)
          .single();

        const userName = userData?.nickname || userData?.name || 
          `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim() || 
          t('events.details.unknownUser');

        // Utwórz wiadomość systemową o dobrowolnym opuszczeniu
        await SingleEvent.createSystemMessage(
          eventId,
          t('events.chat.system.userLeft', { name: userName })
        );
      }

      // Poczekaj chwilę, aby baza danych zdążyła się zaktualizować
      await new Promise(resolve => setTimeout(resolve, 100));

      // Powiadom parent component o zmianie statusu
      if (onStatusChange) {
        await onStatusChange();
      }

      // Stan zostanie zaktualizowany przez real-time subscription
    } catch (err) {
      console.error('Error withdrawing application:', err);
      alert(err.message || t('events.leave.error'));
    }
  };

  // Obsługa akceptacji zgłoszenia (tylko dla hosta)
  const handleAcceptApplication = async (application) => {
    if (!eventIsOpenForParticipation) {
      alert(t('events.applications.eventClosed'));
      return;
    }

    if (!window.confirm(t('events.applications.confirmAccept'))) {
      return;
    }

    try {
      // Sprawdź czy event jest pełny
      if (virtualState.participantsCount >= virtualState.maxParticipants) {
        alert(t('events.applications.eventFull'));
        return;
      }

      // Zaktualizuj status zgłoszenia
      await supabase
        .from('event_applications')
        .update({ status: 'approved', updated_at: new Date().toISOString() })
        .eq('id', application.id);

      // Dodaj do uczestników lub zaktualizuj istniejący rekord
      // Sprawdź czy rekord już istnieje
      const { data: existingParticipant } = await supabase
        .from('event_participants')
        .select('id')
        .eq('event_id', eventId)
        .eq('user_id', application.user_id)
        .maybeSingle();

      if (existingParticipant) {
        // Zaktualizuj istniejący rekord
        const { error: updateError } = await supabase
          .from('event_participants')
          .update({ 
            status: 'active',
            updated_at: new Date().toISOString()
          })
          .eq('id', existingParticipant.id);

        if (updateError) throw updateError;
      } else {
        // Dodaj nowy rekord
        const { error: insertError } = await supabase
          .from('event_participants')
          .insert({
            event_id: eventId,
            user_id: application.user_id,
            status: 'active'
          });

        if (insertError) throw insertError;
      }

      // Pobierz dane użytkownika do wiadomości systemowej
      const { data: userData } = await supabase
        .from('users')
        .select('nickname, name, first_name, last_name')
        .eq('id', application.user_id)
        .single();

      const userName = userData?.nickname || userData?.name || 
        `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim() || 
        t('events.details.unknownUser');

      // Utwórz wiadomość systemową o dołączeniu
      await SingleEvent.createSystemMessage(
        eventId,
        t('events.chat.system.userJoined', { name: userName })
      );

      // UWAGA: Powiadomienie dla użytkownika o akceptacji jest tworzone automatycznie przez trigger w bazie danych
      // (trigger_notify_application_status_change w pliku 012-create-notifications-table.sql)
      // Nie tworzymy tutaj powiadomienia, żeby uniknąć duplikacji

      // Stan zostanie zaktualizowany przez real-time subscription
    } catch (err) {
      console.error('Error accepting application:', err);
      alert(err.message || t('events.applications.errorAccept'));
    }
  };

  // Obsługa odrzucenia zgłoszenia (tylko dla hosta)
  const handleRejectApplication = async (application) => {
    if (!eventIsOpenForParticipation) {
      alert(t('events.applications.eventClosed'));
      return;
    }

    if (!window.confirm(t('events.applications.confirmReject'))) {
      return;
    }

    try {
      await supabase
        .from('event_applications')
        .update({ status: 'rejected', updated_at: new Date().toISOString() })
        .eq('id', application.id);

      // Stan zostanie zaktualizowany przez real-time subscription
    } catch (err) {
      console.error('Error rejecting application:', err);
      alert(err.message || t('events.applications.errorReject'));
    }
  };

  // Obsługa wyrzucenia uczestnika (tylko dla hosta)
  const handleKickParticipant = async (participant) => {
    if (!eventIsOpenForParticipation) {
      alert(t('events.participants.eventClosed'));
      return;
    }

    if (!window.confirm(t('events.participants.confirmKick'))) {
      return;
    }

    try {
      // Get current user (host) info for notification
      const { data: { user: currentUserAuth } } = await supabase.auth.getUser();
      if (!currentUserAuth) {
        throw new Error('User not authenticated');
      }

      // Get host profile info
      const { data: hostProfile } = await supabase
        .from('users')
        .select('name, nickname, first_name, last_name')
        .eq('id', currentUserAuth.id)
        .single();

      // Get event title
      const eventTitle = event?.title || virtualState.event?.title || '';

      await supabase
        .from('event_participants')
        .update({ status: 'kicked', updated_at: new Date().toISOString() })
        .eq('id', participant.id);

      // Zaktualizuj również zgłoszenie jeśli istnieje
      await supabase
        .from('event_applications')
        .update({ status: 'rejected', updated_at: new Date().toISOString() })
        .eq('event_id', eventId)
        .eq('user_id', participant.user_id);

      // Pobierz dane użytkownika do wiadomości systemowej
      const user = participant.user;
      const userName = user?.nickname || user?.name || 
        `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 
        t('events.details.unknownUser');

      // Utwórz wiadomość systemową o wyrzuceniu (różny komunikat niż dobrowolne opuszczenie)
      await SingleEvent.createSystemMessage(
        eventId,
        t('events.chat.system.userKicked', { name: userName })
      );

      // Create notification for kicked user
      const kickerName = hostProfile?.nickname || 
                        hostProfile?.name || 
                        `${hostProfile?.first_name || ''} ${hostProfile?.last_name || ''}`.trim() || 
                        'Organizator';

      const notificationResult = await createNotification({
        userId: participant.user_id,
        type: 'participant_kicked',
        data: {
          event_title: eventTitle,
          kicker_name: kickerName
        },
        eventId: eventId,
        skipCooldown: true // Allow immediate notification
      });

      // Log notification result for debugging
      if (!notificationResult.success) {
        console.error('Failed to create kick notification:', notificationResult);
        if (notificationResult.error) {
          console.error('Notification error details:', {
            code: notificationResult.error.code,
            message: notificationResult.error.message,
            details: notificationResult.error.details,
            hint: notificationResult.error.hint
          });
        }
        // Don't throw error - kicking should succeed even if notification fails
      }

      // Stan zostanie zaktualizowany przez real-time subscription
    } catch (err) {
      console.error('Error kicking participant:', err);
      alert(err.message || t('events.participants.errorKick'));
    }
  };

  if (virtualState.loading) {
    return (
      <div className="event-users-list">
        <Loading className="event-users-list-loading" size="medium" />
      </div>
    );
  }

  if (virtualState.error) {
    return (
      <div className="event-users-list">
        <div className="event-users-list-error">
          <p>{virtualState.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="event-users-list">
      {/* Action Bar - przycisk dołącz i status */}
      {!isHost && currentUser && eventIsOpenForParticipation && (
        <EventUsersActionBar
          event={virtualState.event}
          userApplication={virtualState.userApplication}
          isParticipant={virtualState.isParticipant}
          participantsCount={virtualState.participantsCount}
          maxParticipants={virtualState.maxParticipants}
          onJoin={handleJoin}
          onWithdraw={handleWithdraw}
        />
      )}

      {/* Lista oczekujących w kolejce (tylko dla hosta) */}
      {isHost && eventIsOpenForParticipation && virtualState.pendingApplications.length > 0 && (
        <PendingApplicationsList
          applications={virtualState.pendingApplications}
          onAccept={handleAcceptApplication}
          onReject={handleRejectApplication}
        />
      )}

      {/* Lista dołączonych użytkowników */}
      <JoinedUsersList
        eventId={eventId}
        participants={virtualState.participants}
        participantsCount={virtualState.participantsCount}
        maxParticipants={virtualState.maxParticipants}
        hostId={virtualState.event?.host_id}
        isHost={isHost}
        isParticipant={virtualState.isParticipant}
        userApplication={virtualState.userApplication}
        currentUser={currentUser}
        eventIsOpen={eventIsOpenForParticipation}
        onKick={handleKickParticipant}
        onJoin={handleJoin}
        onStatusChange={onStatusChange}
      />
    </div>
  );
}

export default EventUsersList;
